import type { AnyRoute, RouteInput, RouteRes } from './contract';
import { ApiError, ApiErrorBodyZ } from './errors';

/**
 * Typed fetch client that works the same in the browser, in vitest and in Playwright.
 * It needs no DOM types: pass any fetch with this shape (window.fetch, globalThis.fetch,
 * or a wrapper around Hono's `app.request`).
 */
export interface FetchResponseLike {
  readonly status: number;
  readonly ok: boolean;
  readonly headers: { get(name: string): string | null };
  text(): Promise<string>;
}
export interface FetchInitLike {
  method: string;
  headers: Record<string, string>;
  body?: unknown;
  credentials?: 'include' | 'same-origin' | 'omit';
  signal?: unknown;
}
export type FetchLike = (url: string, init: FetchInitLike) => Promise<FetchResponseLike>;

export interface ApiClientOptions {
  /** '' for same-origin in the app, 'http://localhost:8787' in tests. */
  baseUrl?: string;
  fetch: FetchLike;
  /** Extra headers on every call. The app sets X-TTS-Display and X-TTS-Client here. */
  headers?: () => Record<string, string>;
  /** Parse every response with the route's `res` schema. Default true. */
  validateResponses?: boolean;
  /** Validate request bodies before sending. Default true. */
  validateRequests?: boolean;
  /** Sees every response (for X-App-Version, clock offset from Date, logging). */
  onResponse?: (res: FetchResponseLike, route: AnyRoute) => void;
  credentials?: 'include' | 'same-origin' | 'omit';
}

export interface CallOptions {
  signal?: unknown;
  headers?: Record<string, string>;
}

export interface ApiClient {
  call<C extends AnyRoute>(route: C, input: RouteInput<C>, opts?: CallOptions): Promise<RouteRes<C>>;
  /** Full URL for a route (SSE streams, file downloads, links). */
  url<C extends AnyRoute>(route: C, input?: Pick<RouteInput<C>, 'params' | 'query'>): string;
}

type QueryValue = string | number | boolean | null | undefined | readonly (string | number | boolean)[];

/** Serialize a query object: booleans as 1/0, arrays as repeated keys, nullish skipped. */
export function encodeQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const parts: string[] = [];
  const enc = (v: string | number | boolean) =>
    encodeURIComponent(typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v as readonly (string | number | boolean)[])
        parts.push(`${encodeURIComponent(k)}=${enc(item)}`);
    } else {
      parts.push(`${encodeURIComponent(k)}=${enc(v as string | number | boolean)}`);
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Fill `:param` segments. Throws when one is missing. */
export function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === '') throw new Error(`Missing path param :${name} for ${path}`);
    return encodeURIComponent(v);
  });
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const base = (opts.baseUrl ?? '').replace(/\/$/, '');
  const validateRes = opts.validateResponses ?? true;
  const validateReq = opts.validateRequests ?? true;

  function url<C extends AnyRoute>(route: C, input?: Pick<RouteInput<C>, 'params' | 'query'>): string {
    const i = (input ?? {}) as { params?: Record<string, string>; query?: Record<string, QueryValue> };
    return base + fillPath(route.path, i.params) + encodeQuery(i.query);
  }

  async function call<C extends AnyRoute>(
    route: C,
    input: RouteInput<C>,
    callOpts: CallOptions = {},
  ): Promise<RouteRes<C>> {
    if (route.kind === 'sse' || route.kind === 'file') {
      throw new Error(`${route.name} is a ${route.kind} route: use client.url() with EventSource or a link`);
    }
    const i = input as {
      params?: Record<string, string>;
      query?: Record<string, QueryValue>;
      body?: unknown;
      form?: unknown;
    };
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...(opts.headers?.() ?? {}),
      ...(callOpts.headers ?? {}),
    };
    let body: unknown;
    if (route.kind === 'multipart') {
      body = i.form;
    } else if (route.body) {
      if (validateReq) {
        const parsed = route.body.safeParse(i.body);
        if (!parsed.success) {
          throw new ApiError('VALIDATION', `Request for ${route.name} does not match its contract`, {
            status: 400,
            details: parsed.error.issues,
          });
        }
      }
      headers['content-type'] = 'application/json';
      body = JSON.stringify(i.body);
    }
    const init: FetchInitLike = { method: route.method, headers, credentials: opts.credentials ?? 'same-origin' };
    if (body !== undefined) init.body = body;
    if (callOpts.signal !== undefined) init.signal = callOpts.signal;

    const res = await opts.fetch(
      url(route, { params: i.params, query: i.query } as Pick<RouteInput<C>, 'params' | 'query'>),
      init,
    );
    opts.onResponse?.(res, route);
    const text = res.status === 204 ? '' : await res.text();
    let json: unknown;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = undefined;
      }
    }
    if (!res.ok) {
      const parsed = ApiErrorBodyZ.safeParse(json);
      if (parsed.success) {
        const e = parsed.data.error;
        const o: { status: number; details?: unknown; requestId?: string } = { status: res.status };
        if (e.details !== undefined) o.details = e.details;
        if (e.requestId !== undefined) o.requestId = e.requestId;
        throw new ApiError(e.code, e.message, o);
      }
      throw new ApiError(
        res.status === 428 ? 'INSTALL_REQUIRED' : 'INTERNAL',
        `HTTP ${res.status} from ${route.name}`,
        {
          status: res.status,
        },
      );
    }
    if (res.status === 204) return undefined as RouteRes<C>;
    if (!validateRes) return json as RouteRes<C>;
    const out = route.res.safeParse(json);
    if (!out.success) {
      throw new ApiError('INTERNAL', `Response from ${route.name} does not match its contract`, {
        status: 500,
        details: out.error.issues,
      });
    }
    return out.data as RouteRes<C>;
  }

  return { call, url };
}
